import {
  CCSPrimaryQuestionIds,
  CCSSecondaryQuestionIds,
} from '@/libs/core/src/lib/constants/CCSQuestionIds';
import { SectionTitles } from '@chiroup/core/enums/SectionTitles.enum';
import {
  getQuestionDisplayValue,
  parseTemplateWithClickableSymptoms,
} from '@chiroup/core/functions/buildFlexibleDisplay';
import { ComplaintSummaryType, QuestionAnswer } from '@chiroup/core/types/ComplaintSummary.type';
import React, { useMemo, useRef } from 'react';
import InteractiveValue from './InteractiveValue';
import ProviderDisplay from './ProviderDisplay';
import YellowFlagsDisplay from './YellowFlagsDisplay';

interface TemplateWithInteractiveElementsProps {
  template: string;
  questionData: Record<string, any>;
  questionAnswerMaps: Record<string, QuestionAnswer>;
  isEditable: boolean;
  popoverOpen: Record<string, boolean>;
  onPopoverOpenChange: (uniqueKey: string, open: boolean) => void;
  onAnswerChange: (
    questionId: string,
    newValue: string | string[],
    otherValue?: string,
    customInputData?: Record<string, string>
  ) => void;
  yellowFlags?: string[]; // Add yellow flags prop
  onSymptomClick?: (
    questionId: string,
    symptomValue: string | string[],
    otherValue?: string,
    customInputData?: Record<string, string>
  ) => void;
  summaryType?: ComplaintSummaryType;
  sectionTitle?: string;
  itemIndex?: number;
}

// Helper function to format onset text content
const formatOnsetText = (content: string, index: number) => {
  if (!content.includes('Beginning') || !content.includes('Due to:')) {
    return <span key={index}>{content}</span>;
  }

  const lines = content.split('Due to:');

  if (lines.length !== 2) {
    return <span key={index}>{content}</span>;
  }

  return (
    <span key={index}>
      {lines[0].trim()}
      <br />
      Due to: {lines[1].trim()}
    </span>
  );
};

const TemplateWithInteractiveElements: React.FC<TemplateWithInteractiveElementsProps> = ({
  template,
  questionData,
  questionAnswerMaps,
  isEditable,
  popoverOpen,
  onPopoverOpenChange,
  onAnswerChange,
  yellowFlags,
  onSymptomClick,
  summaryType,
  sectionTitle,
  itemIndex,
}) => {
  // Memoize the expensive template parsing operation
  const templateParts = useMemo(
    () => parseTemplateWithClickableSymptoms(template, questionData),
    [template, questionData]
  );

  // Use useRef to persist the Set across renders without recreating it
  const processedQuestionsRef = useRef<Set<string>>(new Set<string>());

  // Clear the set for each render to track processed questions for this render cycle
  processedQuestionsRef.current.clear();

  // Check if this is a yellow flags template
  const isYellowFlagsTemplate = template.includes(SectionTitles.YellowFlags);

  // Check if this is a provider question template
  // Detect either by explicit label or by presence of a provider questionId placeholder
  const providerQuestionIds = [
    CCSPrimaryQuestionIds.providerQuestionId,
    CCSSecondaryQuestionIds.providerQuestionId,
  ];

  const isProviderTemplate = templateParts.some(
    part =>
      part.type === 'question' &&
      providerQuestionIds.some(pid => (part.questionId || '').includes(pid))
  );

  // Helper function to check if a question is a provider question
  const isProviderQuestion = (questionId: string) => {
    return providerQuestionIds.some(pid => (questionId || '').includes(pid));
  };

  return (
    <span>
      {isYellowFlagsTemplate ? (
        // Show yellow flags with special styling (always show, even if no flags)
        <YellowFlagsDisplay
          yellowFlags={yellowFlags || []}
          questionData={questionData}
          questionAnswerMaps={questionAnswerMaps}
          isEditable={isEditable}
          popoverOpen={popoverOpen}
          onPopoverOpenChange={onPopoverOpenChange}
          onAnswerChange={onAnswerChange}
        />
      ) : isProviderTemplate ? (
        // Show provider question with special handling
        templateParts.map((part, index) => {
          if (part.type === 'text') {
            return formatOnsetText(part.content, index);
          }

          if (part.type === 'question' && part.questionId && part.questionData) {
            const questionAnswerData = questionAnswerMaps[part.questionId];

            if (!questionAnswerData) {
              return (
                <span key={index} className="font-bold text-primary-600">
                  None
                </span>
              );
            }

            // Create a unique key by combining questionId with section title and item index
            const uniqueKey = `${part.questionId}-${sectionTitle || 'default'}-${itemIndex || 0}-${index}`;

            // For provider questions, use ProviderDisplay which uses InteractiveValue
            if (isProviderQuestion(part.questionId || '')) {
              return (
                <ProviderDisplay
                  key={`${part.questionId}-provider`}
                  questionData={questionAnswerData}
                  questionId={part.questionId || ''}
                  isEditable={isEditable}
                  popoverOpen={popoverOpen[uniqueKey] || false}
                  onPopoverOpenChange={open => onPopoverOpenChange(uniqueKey, open)}
                  onAnswerChange={onAnswerChange}
                  summaryType={summaryType}
                />
              );
            }

            // For non-provider questions, use normal handling
            const displayValue = getQuestionDisplayValue(part.questionId, part.questionData);

            // Handle _description fields - show in primary color but non-interactive
            if (part.questionId?.endsWith('_description')) {
              if (Array.isArray(displayValue)) {
                return (
                  <span
                    key={`${part.questionId}-desc-array-provider`}
                    className="font-bold text-primary-600"
                  >
                    {displayValue.join(', ')}
                  </span>
                );
              }

              return (
                <span
                  key={`${part.questionId}-desc-provider`}
                  className="font-bold text-primary-600"
                >
                  {displayValue}
                </span>
              );
            }

            // Handle checkbox values (now returned as comma-separated string)
            if (questionAnswerData.questionType === 'checkbox') {
              return (
                <span key={`${part.questionId}-checkbox-provider`}>
                  {displayValue && displayValue !== '[No response]' ? (
                    <InteractiveValue
                      questionId={part.questionId || ''}
                      questionData={questionAnswerData}
                      displayValue={displayValue}
                      isEditable={isEditable}
                      popoverOpen={popoverOpen[uniqueKey] || false}
                      onPopoverOpenChange={open => onPopoverOpenChange(uniqueKey, open)}
                      onAnswerChange={onAnswerChange}
                    />
                  ) : (
                    <InteractiveValue
                      questionId={part.questionId || ''}
                      questionData={questionAnswerData}
                      displayValue="None"
                      isEditable={isEditable}
                      popoverOpen={popoverOpen[uniqueKey] || false}
                      onPopoverOpenChange={open => onPopoverOpenChange(uniqueKey, open)}
                      onAnswerChange={onAnswerChange}
                    />
                  )}
                </span>
              );
            }

            // Handle single values
            if (displayValue === '[No response]' || displayValue === '') {
              // For numeric/slider questions, show 0 instead of None
              // Only treat slider questions as numeric, not input fields (which could be text descriptions)
              const isNumericQuestion = questionAnswerData.questionType === 'slider';

              return (
                <InteractiveValue
                  key={`${part.questionId}-provider-none`}
                  questionId={part.questionId || ''}
                  questionData={questionAnswerData}
                  displayValue={isNumericQuestion ? '0' : 'None'}
                  isEditable={isEditable}
                  popoverOpen={popoverOpen[uniqueKey] || false}
                  onPopoverOpenChange={open => onPopoverOpenChange(uniqueKey, open)}
                  onAnswerChange={onAnswerChange}
                />
              );
            }

            return (
              <span key={`${part.questionId}-provider-single`}>
                <InteractiveValue
                  questionId={part.questionId || ''}
                  questionData={questionAnswerData}
                  displayValue={displayValue}
                  isEditable={isEditable}
                  popoverOpen={popoverOpen[uniqueKey] || false}
                  onPopoverOpenChange={open => onPopoverOpenChange(uniqueKey, open)}
                  onAnswerChange={onAnswerChange}
                />
              </span>
            );
          }

          return null;
        })
      ) : (
        // Show normal interactive template
        templateParts.map((part, index) => {
          if (part.type === 'text') {
            return formatOnsetText(part.content, index);
          }

          // part example
          // {
          //   type: 'clickable-symptom',
          //   questionId: '62145a16-cef8-41c5-b811-f5baca2e04f9',
          //   symptomValue: 'Back pain',
          //   symptomOtherValue: 'Other',
          //   symptomCustomInputData: { '62145a16-cef8-41c5-b811-f5baca2e04f9': 'Back pain' },
          // }

          if (part.type === 'clickable-symptom' && part.questionId && part.symptomValue) {
            // For clickable symptoms with data-question-id, use onSymptomClick if available
            const questionAnswerData = questionAnswerMaps[part.questionId];

            // Create a wrapper function that calls onSymptomClick with the right signature
            const handleSymptomChange = onSymptomClick
              ? (
                  questionId: string,
                  newValue: string | string[],
                  otherValue?: string,
                  customInputData?: Record<string, string>
                ) => {
                  onSymptomClick(questionId, newValue, otherValue, customInputData);
                }
              : onAnswerChange;

            // Create a unique popover key combining questionId and symptomValue to prevent multiple popovers opening
            const popoverKey = `${part.questionId}-${String(part.symptomValue)}`;

            return (
              <span
                key={popoverKey}
                data-question-id={part.questionId}
                data-symptom-value={part.symptomValue}
                className="clickable-symptom"
              >
                <InteractiveValue
                  questionId={part.questionId || ''}
                  questionData={questionAnswerData || part.questionData}
                  displayValue={part.symptomValue}
                  isEditable={isEditable}
                  popoverOpen={popoverOpen[popoverKey] || false}
                  onPopoverOpenChange={open => onPopoverOpenChange(popoverKey, open)}
                  onAnswerChange={handleSymptomChange}
                />
              </span>
            );
          }

          if (part.type === 'question' && part.questionId && part.questionData) {
            // Handle timing description placeholders first (non-interactive)
            if (part.questionId?.endsWith('_description')) {
              const displayValue = getQuestionDisplayValue(part.questionId, part.questionData);

              if (Array.isArray(displayValue)) {
                return (
                  <span
                    key={`${part.questionId}-desc-array`}
                    className="font-bold text-primary-600"
                  >
                    {displayValue.join(', ')}
                  </span>
                );
              }

              return (
                <span key={`${part.questionId}-desc`} className="font-bold text-primary-600">
                  {displayValue}
                </span>
              );
            }

            // Skip duplicates
            if (processedQuestionsRef.current!.has(part.questionId)) {
              return null;
            }

            processedQuestionsRef.current!.add(part.questionId);

            const questionAnswerData = questionAnswerMaps[part.questionId];

            if (!questionAnswerData) {
              return (
                <span key={`${part.questionId}-missing`} className="font-bold text-primary-600">
                  None
                </span>
              );
            }

            const displayValue = getQuestionDisplayValue(part.questionId, part.questionData);

            // Create a unique key by combining questionId with section title and item index to prevent duplicate popovers
            const uniqueKey = `${part.questionId}-${sectionTitle || 'default'}-${itemIndex || 0}-${index}`;

            // Handle checkbox arrays (multiple values)
            if (Array.isArray(displayValue)) {
              return (
                <span key={`${part.questionId}-checkbox`}>
                  {displayValue.length > 0 ? (
                    displayValue.map((value, valueIndex) => (
                      <React.Fragment key={`${part.questionId}-${valueIndex}`}>
                        <InteractiveValue
                          questionId={part.questionId || ''}
                          questionData={questionAnswerData}
                          displayValue={value}
                          isEditable={isEditable}
                          popoverOpen={popoverOpen[uniqueKey] || false}
                          onPopoverOpenChange={open => onPopoverOpenChange(uniqueKey, open)}
                          onAnswerChange={onAnswerChange}
                        />
                        {valueIndex < displayValue.length - 1 && <br />}
                      </React.Fragment>
                    ))
                  ) : (
                    <InteractiveValue
                      questionId={part.questionId || ''}
                      questionData={questionAnswerData}
                      displayValue="None"
                      isEditable={isEditable}
                      popoverOpen={popoverOpen[uniqueKey] || false}
                      onPopoverOpenChange={open => onPopoverOpenChange(uniqueKey, open)}
                      onAnswerChange={onAnswerChange}
                    />
                  )}
                </span>
              );
            }

            // Handle single values
            if (displayValue === '[No response]' || displayValue === '') {
              // For numeric/slider questions, show 0 instead of None
              // Only treat slider questions as numeric, not input fields (which could be text descriptions)
              const isNumericQuestion = questionAnswerData.questionType === 'slider';

              return (
                <span key={`${part.questionId}-none`}>
                  {' '}
                  <InteractiveValue
                    questionId={part.questionId || ''}
                    questionData={questionAnswerData}
                    displayValue={isNumericQuestion ? '0' : 'None'}
                    isEditable={isEditable}
                    popoverOpen={popoverOpen[uniqueKey] || false}
                    onPopoverOpenChange={open => onPopoverOpenChange(uniqueKey, open)}
                    onAnswerChange={onAnswerChange}
                  />
                </span>
              );
            }

            return (
              <span key={`${part.questionId}-single`}>
                <InteractiveValue
                  questionId={part.questionId || ''}
                  questionData={questionAnswerData}
                  displayValue={displayValue}
                  isEditable={isEditable}
                  popoverOpen={popoverOpen[uniqueKey] || false}
                  onPopoverOpenChange={open => onPopoverOpenChange(uniqueKey, open)}
                  onAnswerChange={onAnswerChange}
                />
              </span>
            );
          }

          return null;
        })
      )}
    </span>
  );
};

export default React.memo(TemplateWithInteractiveElements);
